import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { toast } from "sonner";
import { format, addDays, isSaturday, isSunday, isWeekend } from "date-fns";
import { CalendarBlank, Briefcase, WarningCircle } from "phosphor-react";
import { getDefaultSelectedTechnicians } from "../utils/technicianDefaults";
import AddressSelector from "./AddressSelector";
import { localTimeToUTCISO } from "../utils/calendarTimezone";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper functions
const generateVisitDates = (startDate, numberOfDays, skipWeekends) => {
  const dates = [];
  let currentDate = new Date(startDate);
  
  while (dates.length < numberOfDays) {
    if (!skipWeekends || (!isSaturday(currentDate) && !isSunday(currentDate))) {
      dates.push(new Date(currentDate));
    }
    currentDate = addDays(currentDate, 1);
  }
  
  return dates;
};

const scheduleIncludesWeekend = (startDate, numberOfDays, skipWeekends) => {
  if (skipWeekends) return false;
  
  const dates = [];
  let currentDate = new Date(startDate);
  
  for (let i = 0; i < numberOfDays; i++) {
    dates.push(new Date(currentDate));
    currentDate = addDays(currentDate, 1);
  }
  
  return dates.some(d => isWeekend(d));
};

/**
 * Shared Schedule Visit Modal component
 * Used by both Calendar (empty slot click) and JobDetail (Add Visit button)
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onOpenChange - Callback when modal open state changes
 * @param {Array} props.jobs - List of available jobs to schedule visits for
 * @param {Array} props.technicians - List of assignable technicians
 * @param {Array} props.allTechnicians - List of all technicians (including inactive)
 * @param {Date} props.initialDate - Initial date for the visit (optional)
 * @param {string} props.initialTime - Initial start time (optional, e.g., "09:00")
 * @param {string} props.preselectedJobId - If set, job is pre-selected and locked (used from JobDetail)
 * @param {Object} props.preselectedJob - The full job object when preselected (for getting techs, customer info)
 * @param {function} props.onVisitScheduled - Callback after visit is successfully scheduled
 * @param {Object} props.businessHours - Business hours config { start: "07:00", end: "19:00", timezone: "America/New_York" }
 */
export default function ScheduleVisitModal({
  open,
  onOpenChange,
  jobs = [],
  technicians = [],
  allTechnicians = [],
  initialDate = null,
  initialTime = null,
  preselectedJobId = null,
  preselectedJob = null,
  onVisitScheduled,
  businessHours = { start: "07:00", end: "19:00", timezone: null },
  onBeforeSubmit = null, // Optional callback: (selectedTechIds, proceedCallback) => boolean. Return false to cancel.
}) {
  // Form states
  const [selectedJob, setSelectedJob] = useState("");
  const [visitDate, setVisitDate] = useState(new Date());
  const [visitTime, setVisitTime] = useState("09:00");
  const [visitEndTime, setVisitEndTime] = useState("10:00");
  const [userManuallySetEndTime, setUserManuallySetEndTime] = useState(false); // Track if user changed end time
  const [visitTechs, setVisitTechs] = useState([]);
  const [userTouchedTechs, setUserTouchedTechs] = useState(false);
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  
  // Multi-day visit states
  const [isMultiDay, setIsMultiDay] = useState(false);
  const [numberOfDays, setNumberOfDays] = useState(""); // Empty string for free-form input
  const [numberOfDaysError, setNumberOfDaysError] = useState(null);
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [weekendChoiceMade, setWeekendChoiceMade] = useState(false);
  
  // Address state
  const [selectedCustomerAddresses, setSelectedCustomerAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  
  // Loading state for fetching job data
  const [loadingJobData, setLoadingJobData] = useState(false);
  
  // Tech list collapse state (for long lists)
  const [showAllTechs, setShowAllTechs] = useState(false);
  const TECHS_COLLAPSED_COUNT = 5;
  
  // Date picker popover state
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Parse numberOfDays for calculations (default to 2 for multi-day)
  const parsedNumberOfDays = numberOfDays === "" ? 2 : Math.max(2, parseInt(numberOfDays, 10) || 2);

  // Compute time validation error (not in state to avoid useEffect issues)
  // NOTE: We no longer block endTime < startTime since that indicates an overnight visit
  // Overnight visits (e.g., 10 PM - 1 AM) are valid and the save logic handles the date adjustment
  const timeError = (() => {
    if (isMultiDay) return null;
    // Only validate that times are properly formatted, not that end > start
    const [startH, startM] = visitTime.split(":").map(Number);
    const [endH, endM] = visitEndTime.split(":").map(Number);
    if (isNaN(startH) || isNaN(startM) || isNaN(endH) || isNaN(endM)) {
      return "Invalid time format";
    }
    return null;
  })();

  // Helper to detect overnight visit (end time earlier than start time = crosses midnight)
  const isOvernightVisit = () => {
    const [startH, startM] = visitTime.split(":").map(Number);
    const [endH, endM] = visitEndTime.split(":").map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    return endMinutes < startMinutes;
  };

  // Helper to add 60 minutes to a time string "HH:MM"
  const addMinutesToTime = (timeStr, minutes) => {
    const [h, m] = timeStr.split(":").map(Number);
    let totalMinutes = h * 60 + m + minutes;
    // Clamp to business hours end if provided
    const bhEnd = businessHours.end || "19:00";
    const [bhEndH, bhEndM] = bhEnd.split(":").map(Number);
    const maxMinutes = bhEndH * 60 + bhEndM;
    if (totalMinutes > maxMinutes) totalMinutes = maxMinutes;
    const newH = Math.floor(totalMinutes / 60);
    const newM = totalMinutes % 60;
    return `${newH.toString().padStart(2, "0")}:${newM.toString().padStart(2, "0")}`;
  };

  const resetForm = () => {
    setSelectedJob("");
    setVisitDate(new Date());
    setVisitTime("09:00");
    setVisitEndTime("10:00");
    setUserManuallySetEndTime(false);
    setVisitTechs([]);
    setUserTouchedTechs(false);
    setDefaultsApplied(false);
    setIsMultiDay(false);
    setNumberOfDays("");
    setNumberOfDaysError(null);
    setSkipWeekends(true);
    setWeekendChoiceMade(false);
    setSelectedCustomerAddresses([]);
    setSelectedAddressId(null);
    setShowAllTechs(false);
  };

  // Reset form when modal closes - standard modal pattern
  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line
      resetForm();
    }
  }, [open]);

  // Initialize form when modal opens - standard modal pattern
  useEffect(() => {
    if (open) {
      // Set initial date
      if (initialDate) {
        // eslint-disable-next-line
        setVisitDate(initialDate);
      } else {
         
        setVisitDate(new Date());
      }
      
      // Set initial time if provided
      if (initialTime) {
         
        setVisitTime(initialTime);
        // Set end time to 1 hour after start
        const [h, m] = initialTime.split(":");
        const endHour = parseInt(h) + 1;
        const endHourStr = endHour.toString().padStart(2, "0");
         
        setVisitEndTime(`${endHourStr}:${m}`);
      } else {
        // Use business hours start as default
         
        setVisitTime(businessHours.start || "09:00");
        // End time 1 hour later
        const [h, m] = (businessHours.start || "09:00").split(":");
        const endHour = parseInt(h) + 1;
        const endHourStr = endHour.toString().padStart(2, "0");
         
        setVisitEndTime(`${endHourStr}:${m}`);
      }
      
      // Set preselected job if provided
      if (preselectedJobId) {
         
        setSelectedJob(preselectedJobId);
      }
    }
  }, [open, initialDate, initialTime, preselectedJobId, businessHours.start]);

  // Apply default technicians when conditions are met
  useEffect(() => {
    if (!open || userTouchedTechs || technicians.length === 0 || defaultsApplied) {
      return;
    }
    
    const selectedJobData = preselectedJob || jobs.find(j => j.job_id === selectedJob);
    if (!selectedJobData) return;
    
    const jobAssignedTechs = selectedJobData.assigned_technicians || [];
    const mostRecentVisitTechs = selectedJobData.most_recent_visit_techs || [];
    
    const defaultTechs = getDefaultSelectedTechnicians({
      jobAssignedTechs,
      mostRecentVisitTechs,
      assignableTechs: technicians,
    });
    
    if (defaultTechs.length > 0) {
      // eslint-disable-next-line
      setVisitTechs(defaultTechs);
       
      setDefaultsApplied(true);
    }
  }, [open, selectedJob, preselectedJob, jobs, technicians, userTouchedTechs, defaultsApplied]);

  // Load customer addresses when job is selected
  useEffect(() => {
    const loadCustomerAddresses = async () => {
      const selectedJobData = preselectedJob || jobs.find(j => j.job_id === selectedJob);
      if (!selectedJobData?.customer_id) {
        setSelectedCustomerAddresses([]);
        setSelectedAddressId(null);
        return;
      }
      
      try {
        const response = await fetch(`${API}/customers/${selectedJobData.customer_id}/addresses`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          const addresses = data.addresses || [];
          setSelectedCustomerAddresses(addresses);
          
          // Priority: most recently used > most recently added
          const mostRecentlyUsed = addresses.find(a => a.id === selectedJobData.most_recent_visit_address_id);
          const mostRecentlyAdded = addresses.length > 0 ? addresses[addresses.length - 1] : null;
          
          setSelectedAddressId(
            mostRecentlyUsed?.id || 
            mostRecentlyAdded?.id || 
            null
          );
        }
      } catch (error) {
        console.error("Error loading customer addresses:", error);
      }
    };
    
    if (selectedJob || preselectedJob) {
      loadCustomerAddresses();
    }
  }, [selectedJob, preselectedJob, jobs]);

  const toggleTech = (techId) => {
    setVisitTechs(prev => 
      prev.includes(techId) ? prev.filter(id => id !== techId) : [...prev, techId]
    );
  };

  const handleTechChange = (techId) => {
    setUserTouchedTechs(true);
    toggleTech(techId);
  };

  // Actual submit logic - extracted so it can be called from CTA callback
  const doScheduleVisit = async () => {
    const jobId = preselectedJobId || selectedJob;
    
    try {
      if (isMultiDay && parsedNumberOfDays > 1) {
        // Validate number of days before submission
        if (numberOfDays === "" || parseInt(numberOfDays, 10) < 2) {
          setNumberOfDaysError("Must be at least 2 days");
          return;
        }
        
        // Multi-day: Use bulk-create endpoint
        const response = await fetch(`${API}/visits/bulk-create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            job_id: jobId,
            start_date: format(visitDate, "yyyy-MM-dd"),
            days: parsedNumberOfDays,
            start_time: visitTime,
            end_time: visitEndTime,
            assigned_technicians: visitTechs,
            skip_weekends: skipWeekends,
            customer_address_id: selectedAddressId,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          const weekendNote = skipWeekends ? " (weekdays only)" : "";
          toast.success(`${data.count} visits scheduled${weekendNote}`);
          onOpenChange(false);
          resetForm();
          onVisitScheduled?.();
        } else {
          const error = await response.json();
          toast.error(error.detail || "Failed to schedule visits");
        }
      } else {
        // Single day: Use regular endpoint with proper timezone conversion
        const companyTimezone = businessHours.timezone;
        
        // Handle overnight visits: if end time is earlier than start time,
        // the visit crosses midnight and end date should be the next day
        let endDate = visitDate;
        if (isOvernightVisit()) {
          endDate = addDays(visitDate, 1);
          console.log('[ScheduleVisit] Overnight visit detected - end date adjusted to next day');
        }
        
        // Convert times to UTC - this may throw if time is in DST gap
        let utcStart, utcEnd;
        try {
          utcStart = localTimeToUTCISO(visitDate, visitTime, companyTimezone);
          utcEnd = localTimeToUTCISO(endDate, visitEndTime, companyTimezone);
        } catch (timeError) {
          if (timeError.isDSTGapError) {
            // DST gap - show user-friendly error
            console.warn('[ScheduleVisit] DST gap time selected:', timeError.message);
            toast.error(timeError.message, { duration: 8000 });
            return;
          }
          throw timeError;
        }
        
        console.log('[ScheduleVisit] Creating single visit:', {
          visitDate,
          visitTime,
          visitEndTime,
          timezone: companyTimezone,
          isOvernight: isOvernightVisit(),
          utcStart,
          utcEnd
        });

        const response = await fetch(`${API}/visits`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            job_id: jobId,
            scheduled_start: utcStart,
            scheduled_end: utcEnd,
            assigned_technicians: visitTechs,
            customer_address_id: selectedAddressId,
          }),
        });

        if (response.ok) {
          toast.success("Visit scheduled");
          onOpenChange(false);
          resetForm();
          onVisitScheduled?.();
        } else {
          const error = await response.json();
          toast.error(error.detail || "Failed to schedule visit");
        }
      }
    } catch (error) {
      toast.error("Failed to schedule visit");
    }
  };

  // Schedule visit - supports both single and multi-day
  const scheduleVisit = () => {
    const jobId = preselectedJobId || selectedJob;
    
    if (!jobId) {
      toast.error("Please select a job");
      return;
    }
    
    // Require visit address if customer has addresses
    if (selectedCustomerAddresses.length > 0 && !selectedAddressId) {
      toast.error("Please select a visit address");
      return;
    }
    
    // Client-side time validation (only check for invalid formats, not overnight)
    if (!isMultiDay && timeError) {
      toast.error(timeError);
      return;
    }

    // Call onBeforeSubmit callback with proceed callback
    // Parent can show CTA and call proceed callback later
    if (onBeforeSubmit) {
      const shouldContinue = onBeforeSubmit(visitTechs, doScheduleVisit);
      if (shouldContinue === false) {
        return; // Parent wants to intercept (e.g., show Spanish CTA)
      }
    }

    // No interception - proceed with submit
    doScheduleVisit();
  };

  // Get the selected job data (for showing customer info, etc.)
  const selectedJobData = preselectedJob || jobs.find(j => j.job_id === selectedJob);
  const effectiveJobId = preselectedJobId || selectedJob;
  const isJobLocked = !!preselectedJobId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="schedule-visit-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">
            Schedule Visit
          </DialogTitle>
        </DialogHeader>
        
        <DialogBody>
          <div className="space-y-4">
            {/* Job Selection - Hidden/disabled when preselected */}
            {isJobLocked ? (
              <div>
                <Label>Job</Label>
                <div className="mt-1 p-3 bg-zinc-100 border border-zinc-200 rounded-md">
                  <div className="flex items-center gap-2">
                    <Briefcase size={16} className="text-zinc-500" />
                    <span className="font-medium">{preselectedJob?.title || "Selected Job"}</span>
                    {preselectedJob?.customer?.name && (
                      <span className="text-zinc-500">({preselectedJob.customer.name})</span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <Label>Select Job *</Label>
                <Select value={selectedJob} onValueChange={setSelectedJob}>
                  <SelectTrigger className="mt-1 h-12 border-2" data-testid="visit-job-select">
                    <SelectValue placeholder="Choose a job..." />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.length > 0 ? jobs.map((job) => (
                      <SelectItem key={job.job_id} value={job.job_id}>
                        <div className="flex items-center gap-2">
                          <Briefcase size={14} />
                          <span>{job.title}</span>
                          <span className="text-zinc-400">({job.customer?.name})</span>
                        </div>
                      </SelectItem>
                    )) : (
                      <SelectItem value="none" disabled>No active jobs</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {jobs.length === 0 && (
                  <p className="text-xs text-zinc-500 mt-1">
                    <Link to="/jobs" className="text-[#FF6600]">Create a job first</Link>
                  </p>
                )}
              </div>
            )}

            {/* Address Selection */}
            {effectiveJobId && (
              <AddressSelector
                customerId={selectedJobData?.customer_id || selectedJobData?.customer?.customer_id}
                addresses={selectedCustomerAddresses}
                selectedAddressId={selectedAddressId}
                onAddressSelect={setSelectedAddressId}
                onAddressAdded={(newAddresses) => setSelectedCustomerAddresses(newAddresses)}
              />
            )}

            <div>
              <Label>Date</Label>
              {/* Mobile: Use native date input for better iOS/Android compatibility */}
              <div className="sm:hidden">
                <Input
                  type="date"
                  value={format(visitDate, "yyyy-MM-dd")}
                  onChange={(e) => {
                    if (e.target.value) {
                      // Parse the date string and create a new date
                      const [year, month, day] = e.target.value.split('-').map(Number);
                      const newDate = new Date(year, month - 1, day);
                      setVisitDate(newDate);
                    }
                  }}
                  className="mt-1 h-12 border-2 w-full"
                  data-testid="visit-date-mobile"
                />
              </div>
              {/* Desktop: Use calendar popover */}
              <div className="hidden sm:block">
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 h-12 border-2 justify-start text-left font-normal">
                      <CalendarBlank size={18} className="mr-2" />
                      {format(visitDate, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent 
                      mode="single" 
                      selected={visitDate} 
                      onSelect={(date) => {
                        if (date) {
                          setVisitDate(date);
                          setDatePickerOpen(false);
                        }
                      }} 
                      initialFocus 
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Multi-day Toggle */}
            <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-md border border-zinc-200">
              <div>
                <Label className="text-sm font-medium">Multi-day Visit</Label>
                <p className="text-xs text-zinc-500">Schedule same time across multiple days</p>
              </div>
              <Switch
                checked={isMultiDay}
                onCheckedChange={(checked) => {
                  setIsMultiDay(checked);
                  if (!checked) {
                    setNumberOfDays("");
                    setWeekendChoiceMade(false);
                  }
                }}
                data-testid="multi-day-toggle"
              />
            </div>

            {isMultiDay && (
              <>
                <div>
                  <Label>Number of Days</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={numberOfDays}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow empty or digits only
                      if (val === "" || /^\d+$/.test(val)) {
                        setNumberOfDays(val);
                        setNumberOfDaysError(null);
                      }
                    }}
                    onBlur={() => {
                      // Validate on blur
                      if (numberOfDays !== "") {
                        const num = parseInt(numberOfDays, 10);
                        if (isNaN(num) || num < 2) {
                          setNumberOfDaysError("Must be at least 2 days");
                        } else if (num > 30) {
                          setNumberOfDays("30");
                          setNumberOfDaysError(null);
                        } else {
                          setNumberOfDaysError(null);
                        }
                      }
                    }}
                    placeholder="2"
                    className={`mt-1 h-12 border-2 ${numberOfDaysError ? 'border-red-500' : ''}`}
                    data-testid="multi-day-count"
                  />
                  {numberOfDaysError && (
                    <p className="text-xs text-red-500 mt-1">{numberOfDaysError}</p>
                  )}
                  {!numberOfDaysError && (
                    (() => {
                      const previewDates = generateVisitDates(visitDate, parsedNumberOfDays, skipWeekends);
                      const firstDate = previewDates[0];
                      const lastDate = previewDates[previewDates.length - 1];
                      return (
                        <p className="text-xs text-zinc-500 mt-1">
                          This will create {parsedNumberOfDays} visits from {format(firstDate, "EEE, MMM d")} to {format(lastDate, "EEE, MMM d, yyyy")}
                          {skipWeekends && " (weekdays only)"}
                        </p>
                      );
                    })()
                  )}
                </div>
                
                {/* Conditional Weekend Prompt */}
                {scheduleIncludesWeekend(visitDate, parsedNumberOfDays, false) && !weekendChoiceMade && (
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-md border border-amber-200" data-testid="weekend-prompt">
                    <p className="text-sm text-amber-800">
                      This schedule includes a weekend. Skip weekends?
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSkipWeekends(false);
                          setWeekendChoiceMade(true);
                        }}
                        className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                        data-testid="include-weekends-btn"
                      >
                        Include weekends
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          setSkipWeekends(true);
                          setWeekendChoiceMade(true);
                        }}
                        className="text-xs bg-[#FF6600] hover:bg-[#CC5200] text-white"
                        data-testid="skip-weekends-btn"
                      >
                        Skip weekends
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Show current weekend setting after choice */}
                {weekendChoiceMade && scheduleIncludesWeekend(visitDate, parsedNumberOfDays, false) && (
                  <p className="text-xs text-zinc-500">
                    {skipWeekends ? "Weekends will be skipped" : "Weekends will be included"}
                    <button
                      type="button"
                      onClick={() => setWeekendChoiceMade(false)}
                      className="ml-2 text-[#FF6600] hover:underline"
                    >
                      Change
                    </button>
                  </p>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input 
                  type="time" 
                  value={visitTime} 
                  onChange={(e) => {
                    const newStartTime = e.target.value;
                    setVisitTime(newStartTime);
                    // Auto-set end time to start + 60 min if user hasn't manually changed it
                    if (!userManuallySetEndTime) {
                      setVisitEndTime(addMinutesToTime(newStartTime, 60));
                    }
                  }} 
                  className="mt-1 h-12 border-2" 
                  data-testid="visit-start-time"
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input 
                  type="time" 
                  value={visitEndTime} 
                  onChange={(e) => {
                    setVisitEndTime(e.target.value);
                    setUserManuallySetEndTime(true); // Mark as manually changed
                  }} 
                  className={`mt-1 h-12 border-2 ${timeError ? 'border-red-500' : ''}`}
                  data-testid="visit-end-time"
                />
              </div>
            </div>
            
            {/* Time validation error */}
            {timeError && !isMultiDay && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded-md">
                <WarningCircle size={16} />
                <span>{timeError}</span>
              </div>
            )}

            {/* Technicians list - NO nested scroll, expands naturally in modal body */}
            <div>
              <Label>Assign Technicians</Label>
              <div className="mt-2 border border-zinc-200 rounded-md divide-y divide-zinc-100">
                {(() => {
                  const selectedInactive = allTechnicians.filter(
                    t => t.is_active === false && visitTechs.includes(t.technician_id)
                  );
                  
                  const displayTechs = [...technicians, ...selectedInactive];
                  
                  if (displayTechs.length === 0) {
                    return (
                      <p className="text-sm text-zinc-400 p-3">No technicians. <Link to="/technicians" className="text-[#FF6600]">Add some</Link></p>
                    );
                  }
                  
                  // Determine which techs to show based on collapse state
                  const needsCollapse = displayTechs.length > TECHS_COLLAPSED_COUNT;
                  const visibleTechs = needsCollapse && !showAllTechs 
                    ? displayTechs.slice(0, TECHS_COLLAPSED_COUNT)
                    : displayTechs;
                  const hiddenCount = displayTechs.length - TECHS_COLLAPSED_COUNT;
                  
                  return (
                    <>
                      {visibleTechs.map((tech) => {
                        const isInactive = tech.is_active === false;
                        const isSelected = visitTechs.includes(tech.technician_id);
                        
                        return (
                          <button
                            type="button"
                            key={tech.technician_id}
                            onClick={() => {
                              if (isInactive && !isSelected) return;
                              handleTechChange(tech.technician_id);
                            }}
                            disabled={isInactive && !isSelected}
                            className={`w-full flex items-center gap-3 py-3 px-3 text-left min-h-[44px] touch-manipulation transition-colors ${
                              isInactive 
                                ? 'bg-zinc-50 opacity-60 cursor-not-allowed' 
                                : isSelected
                                  ? 'bg-orange-50'
                                  : 'hover:bg-zinc-50 active:bg-zinc-100'
                            }`}
                            aria-checked={isSelected}
                            role="checkbox"
                            data-testid={`tech-row-${tech.technician_id}`}
                          >
                            <Checkbox 
                              checked={isSelected} 
                              disabled={isInactive && !isSelected}
                              onCheckedChange={() => {
                                // Handle checkbox click directly
                                if (isInactive && !isSelected) return;
                                handleTechChange(tech.technician_id);
                              }}
                            />
                            <span className="flex-1 text-sm">
                              {tech.name}
                              {isInactive && (
                                <span className="text-xs text-red-500 ml-1">(inactive)</span>
                              )}
                            </span>
                            {tech.is_owner && <Badge variant="secondary" className="text-xs px-1.5 py-0.5">Owner</Badge>}
                            {tech.preferred_language === "es" && <Badge variant="secondary" className="text-xs px-1.5 py-0.5">ES</Badge>}
                          </button>
                        );
                      })}
                      
                      {/* Show more/less button for long lists */}
                      {needsCollapse && (
                        <button
                          type="button"
                          onClick={() => setShowAllTechs(!showAllTechs)}
                          className="w-full py-2.5 px-3 text-sm text-[#FF6600] font-medium hover:bg-orange-50 active:bg-orange-100 transition-colors touch-manipulation"
                          data-testid="toggle-tech-list"
                        >
                          {showAllTechs 
                            ? "Show less" 
                            : `Show all technicians (${hiddenCount} more)`
                          }
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => { onOpenChange(false); resetForm(); }} 
            className="flex-1 sm:flex-none border-2"
            data-testid="cancel-schedule-visit"
          >
            Cancel
          </Button>
          <Button 
            onClick={scheduleVisit} 
            disabled={!effectiveJobId || (isMultiDay && numberOfDaysError) || (!isMultiDay && timeError)} 
            className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white" 
            data-testid="confirm-schedule-visit"
          >
            {isMultiDay && parsedNumberOfDays > 1 ? `Schedule ${parsedNumberOfDays} Visits` : "Schedule Visit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
